// src/services/tenantStorageService.js
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Tenant-scoped Firebase Storage service
 * Ensures all file uploads are isolated by tenant ID
 */
class TenantStorageService {
  constructor() {
    this.currentTenantId = null;
  }

  /**
   * Set the current tenant context for file operations
   */
  setTenantContext(tenantId) {
    if (!tenantId) {
      console.error('❌ TenantStorageService: Cannot set empty tenant context');
      throw new Error('Tenant ID is required');
    }
    
    this.currentTenantId = tenantId;
    console.log(`🗂️ TenantStorageService: Switched to tenant "${tenantId}"`);
  }

  /**
   * Get tenant-scoped storage path
   */
  _getTenantPath(category, fileName) {
    if (!this.currentTenantId) {
      throw new Error('Tenant context not set. Call setTenantContext() first.');
    }
    
    return `tenants/${this.currentTenantId}/${category}/${fileName}`;
  }

  /**
   * Upload file to tenant-scoped storage
   * @param {File} file - The file to upload
   * @param {string} category - Storage category (logos, documents, staff-photos, client-files)
   * @param {string} customFileName - Optional custom filename
   * @returns {Promise<{url: string, path: string}>}
   */
  async uploadFile(file, category = 'uploads', customFileName = null) {
    if (!file) {
      throw new Error('File is required');
    }

    if (!this.currentTenantId) {
      throw new Error('Tenant context not set');
    }

    // Check if user is authenticated
    const { auth } = await import('../config/firebase');
    const { getAuth } = await import('firebase/auth');
    const currentUser = getAuth().currentUser;
    
    if (!currentUser) {
      throw new Error('User must be authenticated to upload files');
    }

    // Ensure token is fresh
    try {
      await currentUser.getIdToken(true); // Force refresh
    } catch (tokenError) {
      console.error('Error refreshing auth token:', tokenError);
      throw new Error('Authentication token refresh failed');
    }

    const fileName = customFileName || `${Date.now()}_${file.name}`;
    const filePath = this._getTenantPath(category, fileName);
    const storageRef = ref(storage, filePath);

    try {
      console.log(`📤 Uploading file to: ${filePath}`);
      console.log(`🔐 User authenticated: ${currentUser.email}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        path: filePath,
        name: fileName,
        size: file.size,
        type: file.type
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        filePath,
        userEmail: currentUser?.email
      });
      throw error;
    }
  }

  /**
   * Delete file from tenant-scoped storage
   * @param {string} filePath - The full storage path to delete
   */
  async deleteFile(filePath) {
    if (!filePath) {
      throw new Error('File path is required');
    }

    try {
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
      console.log(`🗑️ Deleted file: ${filePath}`);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * List files in tenant-scoped category
   * @param {string} category - Storage category to list
   * @returns {Promise<Array>}
   */
  async listFiles(category) {
    if (!this.currentTenantId) {
      throw new Error('Tenant context not set');
    }

    const categoryPath = `tenants/${this.currentTenantId}/${category}`;
    const storageRef = ref(storage, categoryPath);

    try {
      const result = await listAll(storageRef);
      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return {
            name: itemRef.name,
            path: itemRef.fullPath,
            url: url
          };
        })
      );
      
      return files;
    } catch (error) {
      console.error('Error listing files:', error);
      throw error;
    }
  }

  /**
   * Upload clinic logo
   * @param {File} logoFile - Logo image file
   * @returns {Promise<{url: string, path: string}>}
   */
  async uploadLogo(logoFile) {
    const fileName = `logo_${Date.now()}.${logoFile.name.split('.').pop()}`;
    return this.uploadFile(logoFile, 'logos', fileName);
  }

  /**
   * Upload staff photo
   * @param {File} photoFile - Staff photo file
   * @param {string} staffId - Staff member ID
   * @returns {Promise<{url: string, path: string}>}
   */
  async uploadStaffPhoto(photoFile, staffId) {
    const fileName = `staff_${staffId}_${Date.now()}.${photoFile.name.split('.').pop()}`;
    return this.uploadFile(photoFile, 'staff-photos', fileName);
  }

  /**
   * Upload client file/document
   * @param {File} file - Document file
   * @param {string} clientId - Client ID
   * @param {string} category - Document category (medical-records, forms, etc.)
   * @returns {Promise<{url: string, path: string}>}
   */
  async uploadClientFile(file, clientId, category = 'documents') {
    const fileName = `client_${clientId}_${category}_${Date.now()}_${file.name}`;
    return this.uploadFile(file, 'client-files', fileName);
  }

  /**
   * Upload general document
   * @param {File} file - Document file
   * @param {string} category - Document category
   * @returns {Promise<{url: string, path: string}>}
   */
  async uploadDocument(file, category = 'general') {
    const fileName = `doc_${category}_${Date.now()}_${file.name}`;
    return this.uploadFile(file, 'documents', fileName);
  }

  /**
   * Get storage usage for current tenant
   * @returns {Promise<{totalFiles: number, categories: Object}>}
   */
  async getStorageUsage() {
    if (!this.currentTenantId) {
      throw new Error('Tenant context not set');
    }

    const categories = ['logos', 'documents', 'staff-photos', 'client-files'];
    const usage = {
      totalFiles: 0,
      categories: {}
    };

    try {
      for (const category of categories) {
        const files = await this.listFiles(category);
        usage.categories[category] = files.length;
        usage.totalFiles += files.length;
      }
      
      return usage;
    } catch (error) {
      console.error('Error getting storage usage:', error);
      return { totalFiles: 0, categories: {} };
    }
  }
}

// Create and export singleton instance
export const tenantStorageService = new TenantStorageService();
